import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useCookies } from "react-cookie";
import versionData from "../version.json";
import { isPharlap } from "../utils/constants/constants";
import { useTranslation } from "react-i18next";
import { UserContext } from "../context/UserContext";
import UtilsService, { PlatformBuildInfo } from "../services/UtilsService";

interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
}

const AboutDialog: React.FC<AboutDialogProps> = ({ open, onClose }) => {
  var sysType: String = "SentioWeb Standard";
  const { t } = useTranslation();
  const userCtx = useContext(UserContext);
  const [cookies] = useCookies(["access_token"]);
  const [platformInfo, setPlatformInfo] = useState<PlatformBuildInfo | null>(null);
  const [platformInfoUnavailable, setPlatformInfoUnavailable] = useState(false);
  const platformField = (value?: string) => {
    if (value && value !== "unknown") return value;
    return platformInfoUnavailable || platformInfo ? "Unavailable" : "Loading...";
  };

  useEffect(() => {
    if (!open) return;

    let active = true;
    setPlatformInfo(null);
    setPlatformInfoUnavailable(false);

    if (!cookies.access_token) {
      setPlatformInfoUnavailable(true);
      return () => {
        active = false;
      };
    }

    UtilsService.getPlatformBuildInfo(cookies.access_token)
      .then((info) => {
        if (active) setPlatformInfo(info);
      })
      .catch(() => {
        if (active) setPlatformInfoUnavailable(true);
      });

    return () => {
      active = false;
    };
  }, [open, cookies.access_token]);

  //console.log ("Build number:", process.env.REACT_APP_BUILD_NUMBER, process.env.REACT_APP_BUILD_DATE)
  if (isPharlap) {
    sysType = "Pharlap";
  }

  const handleReportBug = () => {
    const subject = `Bug report - ${sysType} ${versionData.softwareVersion}`;
    const body = [
      `${t("system_type")}: ${sysType}`,
      `${t("software_variant")}: ${userCtx?.user?.sentio_variant}`,
      `${t("platform_version", { defaultValue: "Platform version" })}: ${platformInfo?.platform_version || "Unavailable"}`,
      `${t("build_date")}: ${platformInfo?.platform_build_date || "Unavailable"}`,
      `${t("build_number")}: ${platformInfo?.platform_build_number || "Unavailable"}`,
      `${t("release_id", { defaultValue: "Release ID" })}: ${platformInfo?.platform_release_id || "Unavailable"}`,
      `${t("application_version", { defaultValue: "Application version" })}: ${versionData.softwareVersion}`,
      `${t("build_date")}: ${process.env.REACT_APP_BUILD_DATE}`,
      `${t("build_number")}: ${process.env.REACT_APP_BUILD_NUMBER}`,
      `${t("release_id", { defaultValue: "Release ID" })}: ${process.env.REACT_APP_GIT_COMMIT}`,
      `User: ${userCtx?.user?.first_name} ${userCtx?.user?.last_name}`,
      `URL: ${window.location.href}`,
      `User agent: ${navigator.userAgent}`,
      "",
      "Please describe the issue below:",
      "",
    ].join("\n");
    const bugReportEmail =
      process.env.REACT_APP_BUG_REPORT_EMAIL || "support@infiniwell.ai";
    const mailtoUrl = `mailto:${bugReportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Anchor-click is more reliable than location.href for triggering the OS mail client
    const link = document.createElement("a");
    link.href = mailtoUrl;
    link.click();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="about-dialog-title">
      <DialogTitle id="about-dialog-title">{t("about_system")}</DialogTitle>
      <DialogContent>
        <Typography>
          {t("system_type")} : {sysType}
        </Typography>
        <Typography>
          {t("software_variant")} : {userCtx?.user?.sentio_variant}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2">
          {t("platform", { defaultValue: "Platform" })}
        </Typography>
        <Typography>
          {t("platform_version", { defaultValue: "Platform version" })} : {platformField(platformInfo?.platform_version)}
        </Typography>
        <Typography>
          {t("build_date")} : {platformField(platformInfo?.platform_build_date)}
        </Typography>
        <Typography>
          {t("build_number")} : {platformField(platformInfo?.platform_build_number)}
        </Typography>
        <Typography>
          {t("release_id", { defaultValue: "Release ID" })} : {platformField(platformInfo?.platform_release_id)}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2">
          {t("application", { defaultValue: "Application" })}
        </Typography>
        <Typography>
          {t("application_version", { defaultValue: "Application version" })} : {versionData.softwareVersion}
        </Typography>
        <Typography>
          {t("build_date")} : {process.env.REACT_APP_BUILD_DATE}
        </Typography>
        <Typography>
          {t("build_number")} : {process.env.REACT_APP_BUILD_NUMBER}
        </Typography>
        <Typography>
          {t("release_id", { defaultValue: "Release ID" })} : {process.env.REACT_APP_GIT_COMMIT}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<BugReportIcon />} onClick={handleReportBug}>
          {t("report_bug")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AboutDialog;
